import { Link } from 'react-router-dom';

import { Icon } from '../components/ui/Icon';
import { ThemeToggle } from '../components/ui/Theme';
import { handoffConfig } from '../handoff/handoff.config';
import {
  paletteCatalog,
  roleCatalog,
  semanticColorCatalog,
} from '../handoff/token-catalog.generated';
import styles from './HandoffPage.module.css';

function getFamilyUsageLabels(familyName: string, familyLabel: string) {
  return roleCatalog
    .filter((role) => role.lightFamily === familyName || role.darkFamily === familyName)
    .map((role) => `${role.label} / ${familyLabel}`);
}

export function HandoffPage() {
  const { viewport } = handoffConfig;

  return (
    <main className={styles.page}>
      <header className={styles.topbar}>
        <Link className={styles.backLink} to="/">
          <Icon name="a-arrow-left" size="sm" />
          返回当前设计
        </Link>
        <div className={styles.topbarActions}>
          <div className={styles.version}>
            <span>{handoffConfig.designStatus}</span>
            <span>v{handoffConfig.version}</span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className={styles.container}>
        <section className={styles.intro}>
          <p>HANDOFF</p>
          <h1>设计交付说明</h1>
          <div className={styles.meta}>
            <span>{handoffConfig.projectName}</span>
            <span>{handoffConfig.build.label}</span>
            <span>{handoffConfig.build.date}</span>
          </div>
        </section>

        <section className={styles.viewportPanel} aria-labelledby="viewport-title">
          <div>
            <p className={styles.sectionLabel}>TARGET VIEWPORT</p>
            <h2 id="viewport-title">PC 桌面端</h2>
          </div>
          <strong>
            {viewport.baseline.width} × {viewport.baseline.height}
          </strong>
          <span>最低宽度 {viewport.minimumWidth}px</span>
        </section>

        <div className={styles.grid}>
          <section className={styles.panel} aria-labelledby="pages-title">
            <div className={styles.panelHeader}>
              <div>
                <p className={styles.sectionLabel}>PAGES</p>
                <h2 id="pages-title">页面入口</h2>
              </div>
              <span>{handoffConfig.pages.length} 个页面</span>
            </div>
            {handoffConfig.pages.map((page) => (
              <Link className={styles.pageLink} key={page.id} to={page.href}>
                <div>
                  <strong>{page.title}</strong>
                  <p>{page.description}</p>
                  <span>{page.states.join(' · ')}</span>
                </div>
                <Icon name="a-arrow-right" size="sm" />
              </Link>
            ))}
          </section>

          <section className={styles.panel} aria-labelledby="interaction-title">
            <div className={styles.panelHeader}>
              <div>
                <p className={styles.sectionLabel}>INTERACTION</p>
                <h2 id="interaction-title">交互边界</h2>
              </div>
            </div>
            <ol className={styles.notes}>
              {handoffConfig.interactions.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>
        </div>

        <section className={styles.panel} aria-labelledby="palette-title">
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.sectionLabel}>PALETTE</p>
              <h2 id="palette-title">物理色阶</h2>
            </div>
            <code>design-system/tokens/palette.json</code>
          </div>
          <div className={styles.paletteFamilies}>
            {paletteCatalog.map((family) => {
              const usageLabels = getFamilyUsageLabels(family.name, family.label);
              const swatches = [...family.swatches].sort(
                (first, second) => Number(first.step) - Number(second.step),
              );

              return (
                <article className={styles.paletteFamily} data-testid={`palette-family-${family.name}`} key={family.name}>
                  <div className={styles.paletteFamilyHeader}>
                    <h3>{family.label}</h3>
                    {usageLabels.length > 0 && (
                      <div className={styles.paletteUsage}>
                        {usageLabels.map((usageLabel) => (
                          <span key={usageLabel}>{usageLabel}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={styles.paletteGrid}>
                    {swatches.map((swatch) => (
                      <div className={styles.paletteItem} key={swatch.cssVariable}>
                        <span
                          aria-hidden="true"
                          className={styles.paletteSwatch}
                          style={{ backgroundColor: `var(${swatch.cssVariable})` }}
                        />
                        <code>{swatch.cssVariable}</code>
                        <span>{swatch.value}</span>
                      </div>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className={styles.panel} aria-labelledby="roles-title">
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.sectionLabel}>ROLE</p>
              <h2 id="roles-title">角色映射</h2>
            </div>
            <span>{roleCatalog.length} 个角色</span>
          </div>
          <div className={styles.tableScroll}>
            <table className={styles.mappingTable}>
              <thead>
                <tr>
                  <th scope="col">角色名称</th>
                  <th scope="col">角色 Key</th>
                  <th scope="col">亮色 Family</th>
                  <th scope="col">暗色 Family</th>
                </tr>
              </thead>
              <tbody>
                {roleCatalog.map((role) => (
                  <tr key={role.name}>
                    <td>{role.label}</td>
                    <td><code>{role.name}</code></td>
                    <td><code>{role.lightFamily}</code></td>
                    <td><code>{role.darkFamily}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.panel} aria-labelledby="semantic-colors-title">
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.sectionLabel}>SEMANTIC</p>
              <h2 id="semantic-colors-title">语义颜色</h2>
            </div>
            <span>{semanticColorCatalog.length} 个颜色</span>
          </div>
          <div className={styles.tableScroll}>
            <table className={`${styles.mappingTable} ${styles.semanticTable}`}>
              <thead>
                <tr>
                  <th scope="col">CSS 变量</th>
                  <th scope="col">中文说明</th>
                  <th scope="col">亮色引用 / 值</th>
                  <th scope="col">暗色引用 / 值</th>
                </tr>
              </thead>
              <tbody>
                {semanticColorCatalog.map((token) => (
                  <tr key={token.name}>
                    <td>
                      <span
                        aria-label={`${token.cssVariable} 当前主题色值`}
                        className={styles.semanticSwatch}
                        role="img"
                        style={{ backgroundColor: `var(${token.cssVariable})` }}
                      />
                      <code>{token.cssVariable}</code>
                    </td>
                    <td>{token.description}</td>
                    <td>
                      <code>{token.lightReference}</code>
                      <span className={styles.resolvedValue}>{token.lightResolved}</span>
                    </td>
                    <td>
                      <code>{token.darkReference}</code>
                      <span className={styles.resolvedValue}>{token.darkResolved}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.panel} aria-labelledby="icons-title">
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.sectionLabel}>ICONFONT SYMBOL</p>
              <h2 id="icons-title">图标与素材</h2>
            </div>
            <span>项目 ID 5225620</span>
          </div>
          <div className={styles.iconGrid}>
            {handoffConfig.icons.map((name) => (
              <div className={styles.iconItem} key={name}>
                <Icon className={styles.neutralIcon} name={name} size="lg" />
                <code>{name}</code>
              </div>
            ))}
          </div>
          <div className={styles.assetList}>
            {handoffConfig.assets.map((asset) => (
              <a
                aria-label={asset.name}
                download={asset.download}
                href={asset.href}
                key={asset.href}
              >
                <div>
                  <strong>{asset.name}</strong>
                  <span>{asset.description}</span>
                </div>
                <Icon name="a-arrow-right" size="sm" />
              </a>
            ))}
          </div>
        </section>

        <section className={styles.devtools} aria-labelledby="devtools-title">
          <div>
            <p className={styles.sectionLabel}>DEVTOOLS</p>
            <h2 id="devtools-title">F12 检查约定</h2>
          </div>
          <p>
            生产构建保留 source map 与可读 CSS Modules 类名。通用颜色、间距、圆角和动效优先追溯到语义变量；页面特定尺寸可就地定义，但不得直接使用物理色板。
          </p>
        </section>
      </div>
    </main>
  );
}
